/**
 * Formatação de data e hora — FONTE ÚNICA do projeto.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * A regra que motiva este arquivo: DATA DE CALENDÁRIO e INSTANTE são coisas
 * semanticamente diferentes, e tratá-las igual é a origem de um bug real.
 *
 *   DATA DE CALENDÁRIO  (nascimento, data da consulta, data do exame, meta)
 *     É um dia no calendário. NÃO tem fuso horário. "09/08/2026" é o dia 9
 *     em qualquer lugar do mundo, e nunca deve ser convertido.
 *
 *   INSTANTE            (created_at, início do evento, resolvido em)
 *     É um ponto no tempo. TEM fuso. Chega em UTC e precisa ser convertido
 *     para o fuso de quem está olhando.
 *
 * Por que isso importa: `new Date("2026-08-09")` é interpretado pelo
 * JavaScript como MEIA-NOITE UTC. No Brasil (UTC-3) isso vira 08/08 às 21h,
 * e a tela mostra o DIA ANTERIOR. Era o que acontecia em 21 lugares do
 * projeto. Na direção oposta, forçar `timeZone: "UTC"` num instante real
 * mostra a hora 3h adiantada — o que acontecia em outros 6.
 *
 * Aqui a classificação é feita pelo VALOR, não pela função:
 *   "2026-08-09"                → data de calendário → lida literalmente
 *   "2026-08-09T00:00:00.000Z"  → data de calendário → lida literalmente  (*)
 *   "2026-08-09T14:30:00.000Z"  → instante           → convertido p/ local
 *
 * (*) O backend serializa casts `'date'` como meia-noite UTC, o que disfarça
 *     uma data de calendário de instante. Enquanto isso não for normalizado
 *     no backend (cast `'date:Y-m-d'`), tratamos meia-noite exata como data
 *     de calendário. O efeito colateral — um instante que caia exatamente em
 *     00:00:00.000Z — é irrelevante na prática e desaparece quando o backend
 *     for ajustado.
 * ───────────────────────────────────────────────────────────────────────────
 */

const LOCALE = "pt-BR";

/** Valor "vazio" que não deve virar data. */
function isEmpty(value) {
  return value === null || value === undefined || value === "";
}

/**
 * É uma data de calendário? (só data, ou data com hora zerada em UTC)
 * Ex.: "2026-08-09" · "2026-08-09 00:00:00" · "2026-08-09T00:00:00.000000Z"
 */
function isCalendarDate(value) {
  if (value instanceof Date) return false;
  return /^\d{4}-\d{2}-\d{2}(?:[T ]00:00:00(?:\.0+)?(?:Z|\+00:?00)?)?$/.test(
    String(value),
  );
}

/** Partes de uma data de calendário, SEM passar por Date (imune a fuso). */
function calendarParts(value) {
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return { year, month, day };
}

/** Converte para Date; devolve null se inválido. */
function toDate(value) {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ═══════════════════════ API pública ═══════════════════════ */

/**
 * Data no formato dd/mm/aaaa.
 * Data de calendário é lida literalmente; instante é convertido para fuso.
 *
 * @param {object}  [opts]
 * @param {string}  [opts.timeZone]  fuso explícito (ex.: o `time_zone` do evento).
 *                                   Ausente = fuso do navegador.
 * @param {string}  [opts.fallback]  o que devolver para valor vazio/inválido.
 */
export function formatDate(value, opts = {}) {
  const { timeZone, fallback = "" } = opts;
  if (isEmpty(value)) return fallback;

  if (isCalendarDate(value)) {
    const { year, month, day } = calendarParts(value);
    return `${day}/${month}/${year}`;
  }

  const d = toDate(value);
  return d ? d.toLocaleDateString(LOCALE, timeZone ? { timeZone } : {}) : fallback;
}

/**
 * Data e hora no formato dd/mm/aaaa HH:mm.
 * Para data de calendário (sem hora real), devolve apenas a data.
 */
export function formatDateTime(value, opts = {}) {
  const { fallback = "" } = opts;
  if (isEmpty(value)) return fallback;
  if (isCalendarDate(value)) return formatDate(value, opts);

  const d = toDate(value);
  if (!d) return fallback;

  // Composto à mão (e não com toLocaleString) para sair "09/08/2026 11:30".
  // O pt-BR do toLocaleString insere vírgula — "09/08/2026, 11:30" — que
  // divergia da maioria das telas.
  return `${formatDate(value, opts)} ${formatTime(value, opts)}`;
}

/**
 * Somente a hora, no formato HH:mm.
 * Aceita também "HH:mm" ou "HH:mm:ss" soltos (comuns em colunas TIME do banco).
 *
 * @param {string}  [opts.timeZone]  fuso explícito (ex.: o do evento)
 * @param {boolean} [opts.hour12]    forçar formato de 12h (AM/PM)
 */
export function formatTime(value, opts = {}) {
  const { timeZone, hour12, fallback = "" } = opts;
  if (isEmpty(value)) return fallback;

  // coluna TIME do banco chega como "14:30:00" — não é uma data, não tem fuso
  const plain = String(value).match(/^(\d{2}):(\d{2})/);
  if (plain) return `${plain[1]}:${plain[2]}`;

  const d = toDate(value);
  if (!d) return fallback;

  return d.toLocaleTimeString(LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
    ...(hour12 !== undefined ? { hour12 } : {}),
  });
}

/** Data abreviada: 09/ago/2026. */
export function formatDateShort(value, opts = {}) {
  const { timeZone, fallback = "" } = opts;
  if (isEmpty(value)) return fallback;

  const d = isCalendarDate(value)
    ? new Date(`${String(value).slice(0, 10)}T12:00:00`) // meio-dia local: imune a fuso
    : toDate(value);
  if (!d) return fallback;

  return d.toLocaleDateString(LOCALE, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(timeZone && !isCalendarDate(value) ? { timeZone } : {}),
  });
}

/** Data por extenso: 9 de agosto de 2026 (com hora, se for um instante). */
export function formatDateLong(value, opts = {}) {
  const { fallback = "" } = opts;
  if (isEmpty(value)) return fallback;

  const calendar = isCalendarDate(value);
  const d = calendar
    ? new Date(`${String(value).slice(0, 10)}T12:00:00`)
    : toDate(value);
  if (!d) return fallback;

  return d.toLocaleDateString(LOCALE, {
    day: "numeric",
    month: "long",
    year: "numeric",
    ...(calendar ? {} : { hour: "2-digit", minute: "2-digit" }),
  });
}

/**
 * Data como "YYYY-MM-DD" no fuso de quem está usando o sistema.
 *
 * Use SEMPRE isto no lugar de `new Date().toISOString().slice(0, 10)`, que é
 * uma armadilha: o `toISOString()` devolve a data em UTC. Um usuário em São
 * Paulo às 22:30 receberia o DIA SEGUINTE; um em Auckland às 08:00, o DIA
 * ANTERIOR. Como esse valor costuma ser gravado no banco (data da consulta,
 * início da meta), o erro vira dado errado, não só tela errada.
 */
export function toDateISO(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Hoje como "YYYY-MM-DD" no fuso local (para inputs type="date"). */
export function todayISO() {
  return toDateISO();
}

/** Fuso horário de quem está usando (ex.: "America/Sao_Paulo", "Europe/Lisbon"). */
export function userTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export default {
  formatDate,
  formatDateTime,
  formatTime,
  formatDateShort,
  formatDateLong,
  toDateISO,
  todayISO,
  userTimeZone,
};
